import React, { ReactNode, useState } from 'react';
import { BringgPopover, PopoverProps, Menu, Tooltip } from '../index';

import './dropdown-menu.scss';

export interface MenuInfo {
	key: React.Key;
	keyPath: React.Key[];
	item: React.ReactInstance;
	domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

export interface DropdownMenuItem {
	key: string | number;
	dataTestId?: string;
	label: ReactNode;
	tooltip?: string;
	onClick?: (info: MenuInfo) => void;
	disabled?: boolean;
	className?: string;
	icon?: ReactNode;
}

interface Props extends Omit<PopoverProps, 'content' | 'popoverType' | 'visible' | 'onVisibleChange'> {
	items: DropdownMenuItem[];
	menuOptions?: {
		selectable?: boolean;
		defaultSelectedKeys?: string[];
	};
	onDropdownVisibleChange?: (visible: boolean) => void;
	onMenuSelect?: (key: string | number) => void;
	'data-test-id'?: string;
	'aria-label'?: string;
}

const DropdownMenu = ({
	items,
	children,
	onMenuSelect,
	overlayClassName,
	trigger,
	menuOptions,
	'aria-label': ariaLabel,
	onDropdownVisibleChange,
	...otherProps
}: Props) => {
	const [visible, setVisible] = useState<boolean>(false);

	const onVisibleChange = (isVisible: boolean) => {
		setVisible(isVisible);
		onDropdownVisibleChange?.(isVisible);
	};

	const menu = (
		<Menu
			aria-label={ariaLabel}
			onClick={({ key }) => {
				onMenuSelect?.(key);
				onVisibleChange(false);
			}}
			className="bringg-dropdown-menu"
			items={items.map(({ key, label, disabled, onClick, dataTestId, tooltip, className = '', icon = '' }) => ({
				key,
				icon,
				label: (
					<Tooltip title={tooltip} placement="right" zIndex={999999}>
						<span data-test-id={`menu-label-${label}`}>{label}</span>
					</Tooltip>
				),
				disabled,
				onClick: args => {
					const { domEvent } = args;
					domEvent.stopPropagation();

					onClick?.(args);
				},
				'data-test-id': dataTestId || `dropdown-menu-item-${key}`,
				className: `bringg-dropdown-menu-item ${className}`
			}))}
			{...menuOptions}
		/>
	);

	return (
		<BringgPopover
			overlayClassName={`bringg-dropdown-menu-overlay ${overlayClassName || ''}`}
			content={menu}
			visible={visible}
			onVisibleChange={onVisibleChange}
			popoverType="dropdown"
			trigger={trigger || ['hover']}
			noArrow
			{...otherProps}
		>
			{children}
		</BringgPopover>
	);
};

export default DropdownMenu;
